import {
  type CreateCapaActionInput,
  Permission,
  CAPA_ACTION_STATUS_LABEL_PT_BR,
  CapaActionStatus,
  type CapaActionListFilter,
} from '@qualicloud/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { CapaActionDialog } from '@/components/sgq/capa-action-dialog';
import { CapaActionStatusBadge, CapaDueBadge } from '@/components/sgq/sgq-badges';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DatePicker,
  EmptyState,
  Field,
  IconClipboardCheck,
  IconFilter,
  IconSearch,
  Input,
  Pagination,
  Popover,
  Select,
  Table,
  cn,
  focusRing,
  parseIso,
  type SortState,
  type TableColumn,
  useToast,
} from '@/design-system';
import { capaApi, queryKeys, requestTypesApi, toUserMessage } from '@/lib/api';
import type { CapaActionView } from '@/lib/api/sgq.types';
import { formatDateOnly } from '@/lib/format/datetime';
import { useDirectory } from '@/lib/use-directory';
import type { CapaActionSearch } from '@/routes/search-schemas';
import { useAuth } from '@/providers/auth-provider';
import { PageBody, PageHeader } from '@/shell/app-shell';

type SortKey = NonNullable<CapaActionListFilter['sortBy']>;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PLANO DE AÇÃO 5W2H — grade de ações corretivas e preventivas
 * sgq.md §C.4
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A PRECEDÊNCIA DE ESCOPO é a do legado, preservada: setor gerenciado >
 * enviadas por mim > atribuídas a mim (`model/reuniao/tarefas.php:528-543`). O
 * que muda é que ela agora é EXPLÍCITA num seletor, em vez de resultar de uma
 * cascata de `if` no servidor que o usuário não tinha como prever.
 *
 * O SEMÁFORO DE PRAZO vem de `resolveCapaDueState` e os quatro baldes são
 * mutuamente exclusivos por construção. No painel do legado eles se SOBREPUNHAM:
 * a mesma ação era contada em mais de um balde e o total ficava maior que o
 * número real de tarefas (BUG-IND-01).
 */
export function CapaActionsPage() {
  const navigate = useNavigate();
  const directory = useDirectory();
  const search = useSearch({ strict: false }) as unknown as CapaActionSearch;

  const filter: Partial<CapaActionListFilter> = useMemo(
    () => ({
      page: search.page ?? 1,
      pageSize: search.pageSize ?? 50,
      scope: (search.scope as CapaActionListFilter['scope']) ?? 'ALL',
      status: search.status as CapaActionStatus[] | undefined,
      nonconformityId: search.nonconformityId,
      capaCaseId: search.capaCaseId,
      meetingId: search.meetingId,
      departmentId: search.departmentId,
      assigneeUserId: search.assigneeUserId,
      // A URL guarda `AAAA-MM-DD`; o filtro do contrato pede `Date`.
      dueFrom: parseIso(search.dueFrom ?? '') ?? undefined,
      dueTo: parseIso(search.dueTo ?? '') ?? undefined,
      onlyOverdue: search.onlyOverdue ?? false,
      onlyAwaitingMyVerification: search.onlyAwaitingMyVerification ?? false,
      search: search.search,
      sortBy: (search.sortBy as SortKey) ?? 'dueDate',
      sortDir: search.sortDir ?? 'asc',
    }),
    [search],
  );

  const updateFilter = useCallback(
    (patch: Partial<CapaActionSearch>) => {
      void navigate({
        to: '/acoes-corretivas',
        search: { ...search, ...patch, page: patch.page ?? 1 } as CapaActionSearch,
      });
    },
    [navigate, search],
  );

  const listQuery = useQuery({
    queryKey: queryKeys.capa.actions(filter),
    queryFn: () => capaApi.listActions(filter),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<Array<TableColumn<CapaActionView, SortKey>>>(
    () => [
      {
        id: 'title',
        header: 'Ação',
        sortKey: 'title',
        width: '30%',
        cell: (row) => (
          <div className="min-w-0">
            <Link
              to="/acoes-corretivas/$actionId"
              params={{ actionId: row.id }}
              className={cn('block truncate font-medium text-brand hover:underline', focusRing)}
            >
              {row.title}
            </Link>
            <p className="mt-0.5 truncate text-xs text-content-subtle">
              Causa-raiz: {row.rootCauseDescription}
            </p>
          </div>
        ),
      },
      {
        id: 'assignee',
        header: 'Responsável',
        width: '16%',
        cell: (row) => (
          <span className="text-sm text-content-muted">{row.assigneeName ?? '—'}</span>
        ),
      },
      {
        id: 'requester',
        header: 'Solicitante',
        width: '15%',
        cell: (row) => (
          <span className="text-sm text-content-muted">
            {row.requesterName ?? '—'}
            <span className="qc-sr-only">
              . É quem verifica a eficácia desta ação quando ela for concluída.
            </span>
          </span>
        ),
      },
      {
        id: 'dueDate',
        header: 'Prazo',
        sortKey: 'dueDate',
        width: '13%',
        cell: (row) => (
          <CapaDueBadge state={row.dueState} status={row.status} dueDate={row.plan.when} />
        ),
      },
      {
        id: 'progress',
        header: 'Progresso',
        width: '10%',
        cell: (row) => (
          <div>
            <span className="text-sm text-content qc-tabular">{row.progressPercent}%</span>
            <div
              aria-hidden="true"
              className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
            >
              <div
                className="h-full rounded-full bg-brand"
                style={{ width: `${Math.min(100, Math.max(0, row.progressPercent))}%` }}
              />
            </div>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Situação',
        sortKey: 'status',
        width: '16%',
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1">
            <CapaActionStatusBadge status={row.status} />
            {row.preventive ? (
              <Badge tone="outline" size="sm">
                Preventiva
              </Badge>
            ) : null}
          </div>
        ),
      },
    ],
    [],
  );

  const { can } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const activeFilters = countActiveFilters(search);

  const [criando, setCriando] = useState(false);
  const [erroCriacao, setErroCriacao] = useState<string | null>(null);

  /*
   * Só os tipos ATIVOS que dispensam causa-raiz — o diálogo filtra de novo, mas
   * carregar tudo aqui e deixar a decisão para lá manteria a mesma regra em
   * dois lugares.
   */
  const requestTypesQuery = useQuery({
    queryKey: queryKeys.requestTypes.all(),
    queryFn: () => requestTypesApi.list(),
  });

  const podeCriar = can(Permission.REQUEST_CREATE);

  const criar = useMutation({
    mutationFn: (input: CreateCapaActionInput) => capaApi.createAction(input),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.capa.all() });
      setCriando(false);
      toast({
        tone: 'success',
        title: 'Solicitação criada',
        description:
          'O responsável foi notificado. Ela não está ligada a nenhuma não-conformidade e não conta no plano de ação de nenhuma.',
      });
      void navigate({ to: '/acoes-corretivas/$actionId', params: { actionId: created.id } });
    },
    onError: (error) => setErroCriacao(toUserMessage(error)),
  });

  const botaoNova = podeCriar ? (
    <Button
      variant="primary"
      onClick={() => {
        setErroCriacao(null);
        setCriando(true);
      }}
    >
      Nova solicitação
    </Button>
  ) : null;

  return (
    <>
      <PageHeader
        title="Solicitações"
        description="Pedidos com plano 5W2H. Podem nascer da análise de causa de uma não-conformidade — e aí são ação corretiva — ou ser abertas avulsas, como pedido de trabalho."
        actions={botaoNova}
      />

      <PageBody>
        <Card padding="sm" className="mb-4">
          <FilterBar
            search={search}
            activeCount={activeFilters}
            departments={directory.departments}
            users={directory.subjects.map((subject) => ({
              id: subject.userId,
              name: subject.displayName,
            }))}
            onChange={updateFilter}
            onClear={() =>
              void navigate({ to: '/acoes-corretivas', search: {} as CapaActionSearch })
            }
          />
        </Card>

        <Table<CapaActionView, SortKey>
          caption="Ações corretivas e preventivas. Colunas: ação, responsável, solicitante, prazo com semáforo de vencimento, progresso e situação."
          columns={columns}
          rows={listQuery.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={listQuery.isPending}
          sort={{ by: (filter.sortBy ?? 'dueDate') as SortKey, dir: filter.sortDir ?? 'asc' }}
          onSortChange={(sort: SortState<SortKey>) =>
            updateFilter({ sortBy: sort.by, sortDir: sort.dir })
          }
          empty={
            <EmptyState
              icon={<IconClipboardCheck size={22} />}
              title={
                activeFilters > 0
                  ? 'Nenhuma ação corresponde aos filtros'
                  : 'Nenhuma ação corretiva registrada'
              }
              description={
                activeFilters > 0
                  ? 'Ajuste ou limpe os filtros para ampliar a busca.'
                  : 'Nenhuma solicitação registrada ainda. A solicitação pode nascer da análise de causa de uma não-conformidade — e aí ela é ação corretiva — ou ser aberta avulsa, como pedido de trabalho.'
              }
              action={
                activeFilters > 0 ? (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      void navigate({ to: '/acoes-corretivas', search: {} as CapaActionSearch })
                    }
                  >
                    Limpar filtros
                  </Button>
                ) : (
                  // Resolve AQUI. O estado vazio mandava o usuário para
                  // /reunioes, o que é um beco: ele saía da tela sem ter feito
                  // o que veio fazer.
                  (botaoNova ?? (
                    <Button asChild variant="secondary">
                      <Link to="/reunioes">Ir para as reuniões</Link>
                    </Button>
                  ))
                )
              }
            />
          }
        />

        {listQuery.data && listQuery.data.total > 0 ? (
          <div className="rounded-b-lg border-x border-b border-border bg-surface">
            <Pagination
              page={listQuery.data.page}
              pageSize={listQuery.data.pageSize}
              total={listQuery.data.total}
              itemNoun="ações"
              onPageChange={(page) => updateFilter({ page })}
              onPageSizeChange={(pageSize) => updateFilter({ pageSize, page: 1 })}
            />
          </div>
        ) : null}
      </PageBody>

      {/*
        O MESMO diálogo que a causa-raiz usa, em modo avulso (`rootCauseId`
        nulo). Dois diálogos separados divergiriam na primeira correção feita em
        só um deles — e a validação do 5W2H é justamente o que não pode divergir
        entre os dois caminhos.
      */}
      {criando ? (
        <CapaActionDialog
          open
          onOpenChange={(next) => {
            if (!next) setCriando(false);
          }}
          rootCauseId={null}
          rootCauseDescription={null}
          requestTypes={requestTypesQuery.data ?? []}
          users={directory.subjects.map((subject) => ({
            id: subject.userId,
            name: subject.displayName,
          }))}
          departments={directory.departments}
          submitting={criar.isPending}
          submitError={erroCriacao}
          onSubmit={(input) => criar.mutate(input)}
        />
      ) : null}
    </>
  );
}

function FilterBar({
  search,
  activeCount,
  departments,
  users,
  onChange,
  onClear,
}: {
  readonly search: CapaActionSearch;
  readonly activeCount: number;
  readonly departments: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly users: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly onChange: (patch: Partial<CapaActionSearch>) => void;
  readonly onClear: () => void;
}) {
  const [term, setTerm] = useState(search.search ?? '');

  return (
    <section aria-label="Filtros do plano de ação" className="flex flex-wrap items-end gap-3">
      <form
        role="search"
        className="min-w-[15rem] flex-1"
        onSubmit={(event) => {
          event.preventDefault();
          onChange({ search: term.trim() || undefined });
        }}
      >
        <label htmlFor="capa-search" className="qc-sr-only">
          Buscar por título da ação
        </label>
        <Input
          id="capa-search"
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Buscar por título da ação…"
          leadingIcon={<IconSearch size={15} />}
        />
      </form>

      <div className="w-64">
        <Field
          label="Escopo"
          description="Precedência: setor que você gerencia, depois as que você solicitou, depois as atribuídas a você."
        >
          <Select
            value={search.scope ?? 'ALL'}
            onValueChange={(value) => onChange({ scope: value })}
            options={[
              { value: 'ALL', label: 'Todas as que posso ver' },
              { value: 'ASSIGNED_TO_ME', label: 'Atribuídas a mim' },
              { value: 'REQUESTED_BY_ME', label: 'Solicitadas por mim' },
              { value: 'DEPARTMENT', label: 'Do setor que eu gerencio' },
            ]}
          />
        </Field>
      </div>

      <div className="w-56">
        <Field label="Situação">
          <Select
            value={search.status?.[0] ?? '__all__'}
            onValueChange={(value) =>
              onChange({ status: value === '__all__' ? undefined : [value] })
            }
            options={[
              { value: '__all__', label: 'Todas as situações' },
              ...Object.values(CapaActionStatus).map((status) => ({
                value: status,
                label: CAPA_ACTION_STATUS_LABEL_PT_BR[status],
              })),
            ]}
          />
        </Field>
      </div>

      <Popover
        width="22rem"
        title="Filtros avançados"
        showClose
        trigger={
          <Button variant="secondary" leadingIcon={<IconFilter size={15} />}>
            Mais filtros
            {activeCount > 0 ? (
              <Badge tone="brand" size="sm" className="ml-1">
                {activeCount}
              </Badge>
            ) : null}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label="Responsável">
            <Select
              value={search.assigneeUserId ?? '__all__'}
              onValueChange={(value) =>
                onChange({ assigneeUserId: value === '__all__' ? undefined : value })
              }
              options={[
                { value: '__all__', label: 'Todos os responsáveis' },
                ...users.map((item) => ({ value: item.id, label: item.name })),
              ]}
            />
          </Field>

          <Field label="Departamento">
            <Select
              value={search.departmentId ?? '__all__'}
              onValueChange={(value) =>
                onChange({ departmentId: value === '__all__' ? undefined : value })
              }
              options={[
                { value: '__all__', label: 'Todos os departamentos' },
                ...departments.map((item) => ({ value: item.id, label: item.name })),
              ]}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Prazo a partir de">
              <DatePicker
                value={search.dueFrom ?? ''}
                onValueChange={(iso) => onChange({ dueFrom: iso || undefined })}
              />
            </Field>
            <Field label="Prazo até">
              <DatePicker
                value={search.dueTo ?? ''}
                onValueChange={(iso) => onChange({ dueTo: iso || undefined })}
              />
            </Field>
          </div>

          <Checkbox
            checked={search.onlyOverdue ?? false}
            onCheckedChange={(checked) => onChange({ onlyOverdue: checked || undefined })}
            label="Apenas vencidas"
            description="Ações ainda abertas cujo prazo já passou."
          />

          <Checkbox
            checked={search.onlyAwaitingMyVerification ?? false}
            onCheckedChange={(checked) =>
              onChange({ onlyAwaitingMyVerification: checked || undefined })
            }
            label="Aguardando minha verificação de eficácia"
            description="Ações concluídas que você solicitou. Quem pediu a ação é quem verifica se o problema acabou."
          />

          {activeCount > 0 ? (
            <Button variant="ghost" onClick={onClear} className="self-start">
              Limpar todos os filtros
            </Button>
          ) : null}
        </div>
      </Popover>

      {search.dueFrom || search.dueTo ? (
        <p className="w-full text-xs text-content-subtle qc-tabular">
          Período de prazo: {search.dueFrom ? formatDateOnly(search.dueFrom) : 'início'} até{' '}
          {search.dueTo ? formatDateOnly(search.dueTo) : 'fim'}.
        </p>
      ) : null}
    </section>
  );
}

function countActiveFilters(search: CapaActionSearch): number {
  let count = 0;
  if (search.status?.length) count += 1;
  if (search.assigneeUserId) count += 1;
  if (search.departmentId) count += 1;
  if (search.search) count += 1;
  if (search.dueFrom) count += 1;
  if (search.dueTo) count += 1;
  if (search.onlyOverdue) count += 1;
  if (search.onlyAwaitingMyVerification) count += 1;
  if (search.nonconformityId) count += 1;
  if (search.meetingId) count += 1;
  if (search.scope && search.scope !== 'ALL') count += 1;
  return count;
}
